import { ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@carbon/react";
import i18next from "../../i18n";
import { useID, useFileInputUploader } from "../../hooks";
import {
  useConfirmDelivery,
  useDelivery,
  useUpdateDeliveryFileUrl,
} from "../../hooks/useDeliveries";
import {
  useMultipleOffersOnOrders,
  useOffersOnOrders,
} from "../../hooks/useOffers";
import { Loader, GoBackArrow } from "../../components";
import { OfferResponse, DeliveryState } from "../../types";
import { ABILITIES } from "../../utils";
import { translateApiError } from "../../utils/translateApiError";
import { useNotification, useUser } from "../../context";
import DeliveryInfoPanel from "./DeliveryInfoPanel";
import PurchaseOrderFilePanel from "./PurchaseOrderFilePanel";
import ConfirmDeliveryDialog, {
  ConfirmDeliveryFormData,
} from "./ConfirmDeliveryDialog";
import "./delivery.scss";

const flattenOffers = (multipleOffers: OfferResponse[][]) => multipleOffers.flat();

const EMPTY_FORM: ConfirmDeliveryFormData = {
  receptionDate: "",
  remittanceNumber: "",
  comment: "",
};

const Delivery = () => {
  const { t } = useTranslation();
  const { isSeller, can } = useUser();
  const { openNotification, openError } = useNotification();
  const navigate = useNavigate();
  const id = useID();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<ConfirmDeliveryFormData>(EMPTY_FORM);

  const { data: delivery, isLoading } = useDelivery(id);
  const { mutate: confirmDelivery, isPending: isConfirming } =
    useConfirmDelivery(
      () => {
        setIsModalOpen(false);
        openNotification(i18next.t("deliveries.confirmSuccess"));
        navigate("/deliveries");
      },
      (error) => openError(error)
    );

  const {
    mutate: uploadFile,
    data: uploadedSpecificationFileUrl,
    isPending: isUploading,
  } = useFileInputUploader();

  const { mutate: updateDeliveryFileUrl, isPending: isUpdatingFileUrl } =
    useUpdateDeliveryFileUrl(
      () => openNotification(i18next.t("deliveries.uploadSuccess")),
      (error) => {
        setUploadError(
          translateApiError(error, {
            fallbackKey: "deliveries.form.validations.uploadError",
          })
        );
        setTimeout(() => setUploadError(null), 5000);
      }
    );

  const { data: offersOnOrders, isLoading: isLoadingOffers } = useOffersOnOrders(
    delivery?.order?.id || ""
  );

  const { data: multipleOffersOnOrders, isLoading: isLoadingMultipleOffers } =
    useMultipleOffersOnOrders(delivery?.order?.multipleOrder?.id || "");

  const multipleOffers = useMemo(() => {
    return multipleOffersOnOrders
      ? (Object.values(multipleOffersOnOrders) as [OfferResponse[]])
      : ([[]] as [OfferResponse[]]);
  }, [multipleOffersOnOrders]);

  const flattenedOffers = useMemo(
    () => flattenOffers(multipleOffers),
    [multipleOffers]
  );

  const selectedOffers = useMemo(() => {
    return flattenedOffers
      .filter((offer: OfferResponse) => offer.selected)
      .sort((a, b) =>
        a.order.material.globalMaterialName.localeCompare(
          b.order.material.globalMaterialName
        )
      );
  }, [flattenedOffers]);

  const selectedOffer = offersOnOrders?.content?.find(
    (el: OfferResponse) => el.selected
  );

  const total = selectedOffers.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const isDeliveryDatePassed = useMemo(() => {
    if (!delivery?.deliveryDate) return false;
    return new Date(delivery.deliveryDate) < new Date();
  }, [delivery?.deliveryDate]);

  const handleConfirmDelivery = () => {
    if (!delivery?.token) return;
    confirmDelivery({
      token: delivery.token,
      receptionDate: formData.receptionDate,
      remittanceNumber: formData.remittanceNumber,
      receiverComment: formData.comment,
    });
    setFormData(EMPTY_FORM);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files && files.length > 0) uploadFile(files[0]);
  };

  const handleUpdateFileUrl = () => {
    if (!uploadedSpecificationFileUrl || !id) return;
    updateDeliveryFileUrl({
      deliveryId: id,
      fileUrl: uploadedSpecificationFileUrl,
    });
  };

  if (isLoading || isLoadingOffers || isLoadingMultipleOffers || isConfirming)
    return <Loader isFullScreen />;

  if (!delivery) return null;

  const canConfirmReceipt =
    can(ABILITIES.MANAGE_DELIVERIES) &&
    isDeliveryDatePassed &&
    delivery.state !== DeliveryState.DELIVERED &&
    delivery.state !== DeliveryState.DRAFT;

  return (
    <>
      {/* Gated on `canConfirmReceipt` — the exact condition under which the trigger below is
          shown — so the confirm flow is reachable only when it should be. This also keeps
          Carbon's `ComposedModal` (which, unlike the old headlessui `Modal`, keeps its DOM
          when closed) from leaking a "Confirm Receipt" primary button into the seller / not-
          yet-due views that e2e pins as `toHaveCount(0)`. */}
      {canConfirmReceipt && (
        <ConfirmDeliveryDialog
          open={isModalOpen}
          formData={formData}
          onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          onConfirm={handleConfirmDelivery}
          onCancel={() => setIsModalOpen(false)}
          isPending={isConfirming}
        />
      )}

      <div className="delivery">
        <div className="delivery__topbar">
          <GoBackArrow />
          {canConfirmReceipt && (
            <Button
              size="md"
              onClick={() => setIsModalOpen(true)}
              disabled={isConfirming}
            >
              {t("deliveries.receiverComment.confirmButton")}
            </Button>
          )}
        </div>

        <div className="delivery__layout">
          <DeliveryInfoPanel
            delivery={delivery}
            isSeller={isSeller}
            selectedOffer={selectedOffer}
            selectedOffers={selectedOffers}
            total={total}
          />
          <div className="delivery__aside">
            <PurchaseOrderFilePanel
              delivery={delivery}
              canUploadPoFile={can(ABILITIES.MANAGE_DELIVERIES)}
              uploadError={uploadError}
              uploadedSpecificationFileUrl={uploadedSpecificationFileUrl}
              isUploading={isUploading}
              isUpdatingFileUrl={isUpdatingFileUrl}
              onFileUpload={handleFileUpload}
              onUpdateFileUrl={handleUpdateFileUrl}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Delivery;
